import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact/enquiry`, formData);
      
      if (response.data.success) {
        toast.success('Thank you! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      }
    } catch (error) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['9316085383', '9755085383'],
      action: 'tel:9316085383',
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['Neelesh@utkarshudyam.com'],
      action: 'mailto:Neelesh@utkarshudyam.com',
    },
    {
      icon: MapPin,
      title: 'Address',
      details: [
        'A/5, Rajshree Avenue',
        'near Dinesh Hall, Navrangpura',
        'Ahmedabad, Gujarat - 380009',
      ],
      action: 'https://maps.google.com/?q=A/5+Rajshree+Avenue+near+Dinesh+Hall+Navrangpura+Ahmedabad',
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Saturday', '9:00 AM - 7:00 PM', 'Sunday Closed'],
      action: null,
    },
  ];

  const services = [
    'Company Registration',
    'GST Registration',
    'Startup India Certificate',
    'ISO Certification',
    'Trademark Registration',
    'Business Loan',
    'FSSAI License',
    'Other Services',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-96 h-96 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#FFB703] text-white px-6 py-2">
              Get In Touch
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Let's Start Your
              <br />
              <span className="text-[#FFB703]">Business Journey Together</span>
            </h1>
            <p className="text-xl text-gray-200">
              Reach out to us for a free consultation
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#0A2463] to-[#247BA0] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-[#0A2463] mb-3">
                      {info.title}
                    </h3>
                    <div className="space-y-1">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-sm text-gray-600">
                          {detail}
                        </p>
                      ))}
                    </div>
                    {info.action && (
                      <a
                        href={info.action}
                        target={info.icon === MapPin ? '_blank' : undefined}
                        rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                      >
                        <Button
                          size="sm"
                          variant="link"
                          className="text-[#247BA0] mt-2"
                        >
                          Contact Now
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-[#0A2463] mb-4">
                  Send Us a Message
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you within 24 hours
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#0A2463] font-semibold">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="mt-2 border-gray-300 focus:border-[#247BA0] focus:ring-[#247BA0]"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#0A2463] font-semibold">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="mt-2 border-gray-300 focus:border-[#247BA0] focus:ring-[#247BA0]"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#0A2463] font-semibold">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="mt-2 border-gray-300 focus:border-[#247BA0] focus:ring-[#247BA0]"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-[#0A2463] font-semibold">
                    Service Interested In *
                  </Label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#247BA0] focus:border-[#247BA0]"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0A2463] font-semibold">
                    Your Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    className="mt-2 border-gray-300 focus:border-[#247BA0] focus:ring-[#247BA0]"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#0A2463] to-[#247BA0] hover:from-[#247BA0] hover:to-[#0A2463] text-white shadow-lg hover:shadow-xl transition-all"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Why Contact Us */}
            <div>
              <div className="bg-gradient-to-br from-[#0A2463] to-[#247BA0] rounded-2xl p-8 text-white shadow-2xl mb-8">
                <h3 className="text-3xl font-bold mb-6">Why Contact Us?</h3>
                <div className="space-y-4">
                  {[
                    'Free Consultation & Eligibility Check',
                    'Expert Guidance from Day 1',
                    'Transparent Pricing - No Hidden Charges',
                    'Fast Processing & Delivery',
                    'Dedicated Relationship Manager',
                    'PAN-India Service Coverage',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2
                        className="text-[#FFB703] mt-1 flex-shrink-0"
                        size={20}
                      />
                      <span className="text-gray-100">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Contact Buttons */}
              <div className="space-y-4">
                <a href="https://wa.me/919316085383" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="w-full bg-[#25d366] hover:bg-[#128c7e] text-white shadow-lg"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    WhatsApp: 9316085383
                  </Button>
                </a>
                <a href="tel:9316085383">
                  <Button
                    size="lg"
                    className="w-full bg-gradient-to-r from-[#FFB703] to-[#FB8500] hover:from-[#FB8500] hover:to-[#FFB703] text-white shadow-lg"
                  >
                    <Phone className="mr-2" size={20} />
                    Call: 9316085383
                  </Button>
                </a>
                <a href="mailto:Neelesh@utkarshudyam.com">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463] hover:text-white shadow-lg"
                  >
                    <Mail className="mr-2" size={20} />
                    Email Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0A2463] mb-4">
                Visit Our Office
              </h2>
              <p className="text-gray-600">
                A/5, Rajshree Avenue, near Dinesh Hall, Navrangpura, Ahmedabad, Gujarat - 380009
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps?q=A%2F5%2C%20Rajshree%20Avenue%2C%20near%20Dinesh%20Hall%2C%20Navrangpura%2C%20Ahmedabad%2C%20Gujarat%20380009&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Utkarshudyam Solution Office Location"
              ></iframe>
            </div>
            <div className="text-center mt-6">
              <a
                href="https://maps.app.goo.gl/kNQpgNwD4Y1AoRw99"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#247BA0] hover:text-[#0A2463] font-semibold"
              >
                <MapPin size={20} />
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;